<template>
  <div class="editor-layout-wrapper">
    <div class="editor-container bg-[#ffffff] dark:bg-gray-700 border-gray-200 dark:border-gray-800" :class="{ 'no-border': !border }">
      <!-- Modular Toolbar -->
      <EditorToolbar v-if="editor && mode === 'edit'" :editor="editor" :disabled="disabled" :semitone="semitone" @comment-added="showCommentsSidebar = true" @transpose="onTranspose" @update:semitone="$emit('update:semitone', $event)" />

      <!-- Editor Area -->
      <div class="editor-content-wrapper bg-[#ffffff] dark:bg-gray-700 text-gray-900 dark:text-white">
        <editor-content :editor="editor" class="editor-content" spellcheck="false" />
      </div>
    </div>
    
    <!-- Comments Sidebar Component -->
    <SongCommentsSidebar
      v-model:visible="showCommentsSidebar"
      v-model:pinned="isCommentsSidebarPinned"
      :comments="commentsList"
      :editable="!isReadOnly"
      :activeCommentId="activeCommentId"
      @select-comment="highlightCommentInDocument"
      @delete-comment="deleteComment"
    />
    
  </div>
</template>

<script setup>
import { defineEmits, defineProps, defineExpose, watch, onMounted, onUnmounted, nextTick } from 'vue';
import { useEditor, EditorContent } from '@tiptap/vue-3';
import StarterKit from '@tiptap/starter-kit';
import { NodeSelection } from '@tiptap/pm/state';
import Underline from '@tiptap/extension-underline';
import Highlight from '@tiptap/extension-highlight';
import Image from '@tiptap/extension-image';
import TextAlign from '@tiptap/extension-text-align';
import { FontSize } from '@/extensions/FontSize.js';
import { Chord } from '@/extensions/Chord.js';
import { FontColor } from '@/extensions/FontColor.js';
import { CustomBlockquote } from '@/extensions/CustomBlockquote.js';
import { Comment } from '@/extensions/Comment.js';
import { MusicSheetExtension } from '@/extensions/MusicSheetExtension.js';
import { VocalClass } from '@/extensions/VocalClass.js';
import { TextCase } from '@/extensions/TextCase.js';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { getSurroundingText, isWordChar, preprocessHtml } from '@/service/textEditor.service.js';
import { appEvents } from '@/store/app.events';
import EditorToolbar from './EditorToolbar.vue';
import SongCommentsSidebar from './SongCommentsSidebar.vue';

const props = defineProps({
  mode: {
    type: String,
    default: 'edit' // 'edit' or 'view'
  },
  disabled: {
    type: Boolean,
    default: false
  },
  border: {
    type: Boolean,
    default: true
  },
  modelValue: {
    type: String,
    default: undefined
  },
  semitone: {
    type: Number,
    default: 0
  }
});

const isReadOnly = computed(() => props.mode === 'view' || props.disabled);

const { t } = useI18n();
const emit = defineEmits(['update', 'update:modelValue', 'update:semitone', 'transpose']);

const onTranspose = (event) => {
  emit('transpose', event);
};
const showCommentsSidebar = ref(false);
const isCommentsSidebarPinned = ref(false);
const activeCommentId = ref(null);

// Flag to prevent watch from calling setContent when the change originated inside the editor.
// Without this, Delete/Backspace triggers onUpdate → emit → parent updates prop → watch calls
// setContent() which resets the cursor to the last line.
const isInternalUpdate = ref(false);

const loadPinnedState = () => {
  try {
    const saved = localStorage.getItem('song-comments-sidebar-pinned');
    if (saved !== null) {
      isCommentsSidebarPinned.value = JSON.parse(saved);
    }
  } catch (e) {
    console.error('Error loading pinned state:', e);
  }
};

const getEditorDom = () => {
  try {
    if (!editor.value || editor.value.isDestroyed || !editor.value.editorView) return null;
    const view = editor.value.editorView;
    return (view && view.dom) ? view.dom : null;
  } catch (e) {
    return null;
  }
};

const highlightCommentInDocument = (id) => {
  const editorDom = getEditorDom();
  if (!editorDom) return;
  activeCommentId.value = id;
  const commentEls = editorDom.querySelectorAll('.comment-mark, [data-comment-id]');
  commentEls.forEach(el => {
    if (el.getAttribute('data-comment-id') === id) {
      el.classList.add('comment-mark-active');
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => {
        el.classList.remove('comment-mark-active');
      }, 2500);
    } else {
      el.classList.remove('comment-mark-active');
    }
  });
};



watch(() => props.modelValue, (newVal) => {
  if (!editor.value) return;
  // Skip if the change came from inside the editor itself (e.g. typing, Delete, Backspace).
  // Calling setContent() in that case would reset the cursor to the end of the document.
  if (isInternalUpdate.value) {
    isInternalUpdate.value = false;
    return;
  }
  const currentContent = editor.value.getHTML();
  const newContent = newVal !== undefined ? preprocessHtml(newVal) : '';
  if (currentContent !== newContent) {
    // Save the cursor position before updating the content (e.g. auto-save response from server).
    // Without this, setContent() always resets the cursor to the end of the document.
    const { from, to } = editor.value.state.selection;
    editor.value.commands.setContent(newContent, false);
    // Restore cursor to the saved position, clamped to the new document size.
    const newDocSize = editor.value.state.doc.content.size;
    const clampedFrom = Math.min(from, newDocSize);
    const clampedTo = Math.min(to, newDocSize);
    try {
      editor.value.commands.setTextSelection({ from: clampedFrom, to: clampedTo });
    } catch (e) {
      // Ignore if selection restoration fails (e.g. document structure changed significantly)
    }
    setTimeout(adjustChordSpacing, 100);
  }

});

watch(isReadOnly, (newVal) => {
  if (editor.value) {
    editor.value.setEditable(!newVal);
  }
});

const isChord = (word) => {
  const chordRegex = /^[A-G][b#]?(m|M|min|maj|dim|aug|sus|add|arr)?[0-9]*(M|maj)?(\+[0-9]*)?(\/[A-G][b#]?)?$/;
  return chordRegex.test(word);
};

const isChordLine = (line) => {
  const trimmed = line.trim();
  if (trimmed.length === 0) return false;
  
  const words = trimmed.split(/\s+/);
  let chordCount = 0;
  for (const word of words) {
    if (isChord(word)) {
      chordCount++;
    }
  }
  return chordCount > 0 && chordCount / words.length >= 0.8;
};

const parsePastedChordsText = (text) => {
  const lines = text.split(/\r?\n/).map(line => line.replace(/([A-Za-z0-9#])(?=[A-G])/g, '$1 '));
  const resultLines = [];
  
  let i = 0;
  while (i < lines.length) {
    const currentLine = lines[i];
    
    if (isChordLine(currentLine) && i + 1 < lines.length && !isChordLine(lines[i + 1])) {
      const chordLine = currentLine;
      const lyricLine = lines[i + 1];
      
      const chords = [];
      const regex = /\S+/g;
      let match;
      while ((match = regex.exec(chordLine)) !== null) {
        if (isChord(match[0])) {
          chords.push({
            chord: match[0],
            index: match.index
          });
        }
      }
      
      chords.sort((a, b) => b.index - a.index);
      
      let mergedLine = lyricLine;
      chords.forEach(({ chord, index }) => {
        if (mergedLine.length < index) {
          mergedLine = mergedLine + ' '.repeat(index - mergedLine.length);
        }
        
        const chordHtml = `<sup data-type="chord" class="chord-sup"><span class="chord-span" draggable="true" contenteditable="false">${chord}</span></sup>`;
        mergedLine = mergedLine.slice(0, index) + chordHtml + mergedLine.slice(index);
      });
      
      resultLines.push(`<p>${mergedLine}</p>`);
      i += 2;
    } else {
      if (isChordLine(currentLine)) {
        const chords = [];
        const regex = /\S+/g;
        let match;
        while ((match = regex.exec(currentLine)) !== null) {
          chords.push(`<sup data-type="chord" class="chord-sup"><span class="chord-span" draggable="true" contenteditable="false">${match[0]}</span></sup>`);
        }
        resultLines.push(`<p>${chords.join(' ')}</p>`);
      } else {
        resultLines.push(`<p>${currentLine || '&nbsp;'}</p>`);
      }
      i++;
    }
  }
  
  return resultLines.join('');
};

const commentsList = computed(() => {
  if (!editor.value) return [];
  const list = [];
  editor.value.state.doc.descendants((node) => {
    if (node.isText && node.marks) {
      node.marks.forEach(mark => {
        if (mark.type.name === 'comment') {
          const id = mark.attrs.id;
          const text = mark.attrs.text;
          const isDup = list.some(c => c.id === id);
          if (!isDup) {
            list.push({
              id,
              commentText: text,
              selectedText: node.text
            });
          }
        }
      });
    }
  });
  return list;
});

const deleteComment = (id) => {
  if (!editor.value || editor.value.isDestroyed) return;
  try {
    let tr = editor.value.state.tr;
    editor.value.state.doc.descendants((node, pos) => {
      if (node.isText && node.marks) {
        node.marks.forEach(mark => {
          if (mark.type.name === 'comment' && mark.attrs.id === id) {
            tr = tr.removeMark(pos, pos + node.nodeSize, mark.type);
          }
        });
      }
    });
    if (editor.value && editor.value.editorView) {
      editor.value.editorView.dispatch(tr);
    }
  } catch (e) {
    console.warn('Failed to delete comment:', e);
  }
};

const adjustChordSpacing = () => {
  const container = getEditorDom();
  if (!container || !editor.value || editor.value.isDestroyed || !editor.value.editorView) return;
  
  // Set class on ProseMirror DOM to temporarily hide spacer ::after elements during measurement
  container.classList.add('measure-chords');
  
  // Force a layout recalculation
  void container.offsetHeight;
  
  const spacerMap = new Map();
  const spans = container.querySelectorAll('span.chord-span');
  console.log('adjustChordSpacing: Found chord spans:', spans.length);
  
  // Group spans by line (vertically close, top difference < 15px)
  const lineGroups = [];
  spans.forEach(span => {
    const rect = span.getBoundingClientRect();
    if (rect.width === 0) return;
    
    // Find an existing group where the top position is within 15px
    let group = lineGroups.find(g => Math.abs(g.top - rect.top) < 15);
    if (!group) {
      group = { top: rect.top, items: [] };
      lineGroups.push(group);
    }
    group.items.push({ span, rect });
  });
  console.log('adjustChordSpacing: Line groups:', lineGroups.length);
  
  // For each line, compute spacing from left to right
  lineGroups.forEach(group => {
    // Sort by DOM document position to guarantee correct flow order (left-to-right in DOM)
    const lineChords = group.items.sort((a, b) => {
      const position = a.span.compareDocumentPosition(b.span);
      if (position & Node.DOCUMENT_POSITION_FOLLOWING) {
        return -1;
      }
      if (position & Node.DOCUMENT_POSITION_PRECEDING) {
        return 1;
      }
      return 0;
    });
    
    for (let i = 0; i < lineChords.length - 1; i++) {
      const current = lineChords[i];
      const next = lineChords[i + 1];
      
      // Since both current and next nodes are shifted by previous spacers identically,
      // we can calculate the overlap using their original bounding rects.
      const currentRight = current.rect.right;
      const nextLeft = next.rect.left;
      
      const requiredGap = 14; // minimum gap in px between chords
      if (currentRight + requiredGap > nextLeft) {
        const overlap = (currentRight + requiredGap) - nextLeft;
        const parentSup = current.span.closest('sup.chord-sup');
        if (parentSup) {
          const { beforeChar, afterChar } = getSurroundingText(parentSup);
          const isWithinWord = isWordChar(beforeChar) && isWordChar(afterChar);
          const spacerChar = isWithinWord ? '_' : ' ';
          
          // Add a temporary hidden spacer character to calculate the exact width needed
          const measureSpan = document.createElement('span');
          measureSpan.style.visibility = 'hidden';
          measureSpan.style.position = 'absolute';
          measureSpan.style.whiteSpace = 'pre';
          const computedStyle = window.getComputedStyle(parentSup);
          measureSpan.style.font = computedStyle.font;
          measureSpan.style.fontWeight = 'bold'; // Match the ::after styling
          measureSpan.textContent = spacerChar;
          document.body.appendChild(measureSpan);
          const charWidth = measureSpan.getBoundingClientRect().width || 8;
          document.body.removeChild(measureSpan);
          
          const numChars = Math.ceil(overlap / charWidth);
          if (numChars > 0) {
            const spacer = spacerChar.repeat(numChars);
            console.log(`adjustChordSpacing: Overlap detected! spacerChar="${spacerChar}", numChars=${numChars}`);
            try {
              if (editor.value && editor.value.editorView) {
                const pos = editor.value.editorView.posAtDOM(parentSup);
                spacerMap.set(pos, spacer);
              }
            } catch (e) {
              console.warn('adjustChordSpacing: posAtDOM failed', e);
            }
          }
        }
      }
    }
  });
  
  // Remove measurement class
  container.classList.remove('measure-chords');
  
  // Apply computed spacers to the Tiptap document state
  let tr = editor.value.state.tr;
  let hasUpdates = false;
  
  editor.value.state.doc.descendants((node, pos) => {
    if (node.type.name === 'chord') {
      const newSpacer = spacerMap.has(pos) ? spacerMap.get(pos) : '';
      if (node.attrs.spacer !== newSpacer) {
        console.log(`adjustChordSpacing: Updating node spacer at pos ${pos} from "${node.attrs.spacer}" to "${newSpacer}"`);
        tr = tr.setNodeMarkup(pos, null, {
          ...node.attrs,
          spacer: newSpacer
        });
        hasUpdates = true;
      }
    }
  });
  
  if (hasUpdates && editor.value && editor.value.editorView) {
    tr.setMeta('isChordSpacing', true);
    editor.value.editorView.dispatch(tr);
  }
};

let resizeObserver = null;

const handleChordClick = (event) => {
  const chordSpan = event.target.closest('.chord-span');
  if (chordSpan) {
    const chordText = chordSpan.textContent.trim();
    if (chordText) {
      appEvents.emit('chordClicked', chordText);
    }
  }
};

const setupEditorListeners = () => {
  const dom = getEditorDom();
  if (!dom) return;
  if (window.ResizeObserver) {
    if (!resizeObserver) {
      resizeObserver = new ResizeObserver(() => {
        adjustChordSpacing();
      });
      resizeObserver.observe(dom);
    }
  } else {
    window.removeEventListener('resize', adjustChordSpacing);
    window.addEventListener('resize', adjustChordSpacing);
  }
  dom.removeEventListener('click', handleChordClick);
  dom.addEventListener('click', handleChordClick);
  adjustChordSpacing();
};

onMounted(async () => {
  await nextTick();
  setupEditorListeners();
  setTimeout(setupEditorListeners, 200);
});

onUnmounted(() => {
  if (resizeObserver) {
    resizeObserver.disconnect();
  } else {
    window.removeEventListener('resize', adjustChordSpacing);
  }
  const dom = getEditorDom();
  if (dom) {
    dom.removeEventListener('click', handleChordClick);
  }
});



// Initialize Editor
const editor = useEditor({
  editable: !isReadOnly.value,
  content: props.modelValue !== undefined ? preprocessHtml(props.modelValue) : `
    <p><strong>${t('editor.welcome')}</strong></p>
    <p>${t('editor.introText')}</p>
    <p>${t('editor.supportFormatting')}</p>
    <p>${t('editor.supportAlign')}</p>
  `,
  extensions: [
    StarterKit.configure({
      bulletList: false,
      orderedList: false,
      heading: false,
      blockquote: false,
    }),
    Underline,
    Highlight.configure({ multicolor: true }),
    Image,
    TextAlign.configure({
      types: ['paragraph'],
    }),
    FontSize,
    Chord,
    FontColor,
    CustomBlockquote,
    Comment,
    VocalClass,
    TextCase,
    MusicSheetExtension,
  ],
  onCreate: () => {
    nextTick(setupEditorListeners);
  },
  onUpdate: ({ transaction }) => {
    // Mark that the next watch trigger comes from inside the editor,
    // so it won't call setContent() and reset the cursor position.
    isInternalUpdate.value = true;
    emit('update', { json: editor.value.getJSON(), html: editor.value.getHTML(), text: editor.value.getText() });
    emit('update:modelValue', editor.value.getHTML());
    if (transaction && transaction.getMeta('isChordSpacing')) return;
    setTimeout(adjustChordSpacing, 10);
  },
  onSelectionUpdate: () => {
    setTimeout(adjustChordSpacing, 10);
  },
  editorProps: {
    handlePaste: (view, event) => {
      const html = event.clipboardData.getData('text/html');
      let text = event.clipboardData.getData('text/plain');
      
      // If the HTML has our custom classes or data attributes, it is a copy-paste from inside the editor.
      // Let ProseMirror handle it natively so we preserve all blockquotes, styles, marks, and nodes.
      if (html && (html.includes('data-type="') || html.includes('class="custom-blockquote') || html.includes('class="chord-sup'))) {
        return false;
      }
      
      if (!text) return false;
      
      // Detect CifraClub's new layout broken copy-paste which injects ">[CHORD]" and duplicates lines in plain text
      if (text.includes('\n">') && html) {
        // Regenerate the plain text directly from the HTML payload, which has the correct visual alignment
        const tempDiv = document.createElement('div');
        // Remove style tags that get exposed as raw text by textContent
        let cleanHtml = html.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
        tempDiv.innerHTML = cleanHtml.replace(/<p[^>]*>/gi, '\n').replace(/<\/p>/gi, '').replace(/<br[^>]*>/gi, '\n');
        text = tempDiv.textContent.replace(/\u00A0/g, ' ').trim();
      }
      
      const lines = text.split(/\r?\n/);
      const hasChords = lines.some(line => isChordLine(line));
      
      if (hasChords) {
        const htmlContent = parsePastedChordsText(text);
        editor.value.commands.insertContent(htmlContent);
        return true;
      }
      return false;
    },
    handleDOMEvents: {
      dragstart: (view, event) => {
        const chordTarget = event.target.closest('.chord-span');
        if (chordTarget) {
          try {
            const pos = view.posAtDOM(chordTarget);
            if (pos !== null && pos !== undefined) {
              const { state, dispatch } = view;
              let selection = null;
              
              const isChordNode = (p) => {
                if (p < 0 || p >= state.doc.content.size) return false;
                const node = state.doc.nodeAt(p);
                return node && node.type.name === 'chord';
              };
              
              let chordPos = -1;
              if (isChordNode(pos)) {
                chordPos = pos;
              } else if (isChordNode(pos - 1)) {
                chordPos = pos - 1;
              } else {
                for (let offset = -3; offset <= 3; offset++) {
                  if (isChordNode(pos + offset)) {
                    chordPos = pos + offset;
                    break;
                  }
                }
              }
              
              if (chordPos !== -1) {
                try {
                  selection = NodeSelection.create(state.doc, chordPos);
                } catch (e) {
                  console.error("Failed to create NodeSelection:", e);
                }
              }
              
              if (selection) {
                dispatch(state.tr.setSelection(selection));
              }
            }
          } catch (err) {
            console.error("Error setting selection on dragstart:", err);
          }
        }

        const sheetHandle = event.target.closest('[data-drag-handle], .music-sheet-drag-handle');
        if (sheetHandle) {
          try {
            const sheetWrapper = event.target.closest('.music-sheet-node-view') || event.target.closest('[data-music-sheet-wrapper]');
            if (sheetWrapper) {
              const pos = view.posAtDOM(sheetWrapper, 0);
              if (pos !== null && pos !== undefined) {
                const { state, dispatch } = view;
                let sheetPos = pos;
                const nodeAtPos = state.doc.nodeAt(pos);
                if (nodeAtPos && nodeAtPos.type.name === 'musicSheet') {
                  sheetPos = pos;
                } else {
                  for (let offset = -4; offset <= 4; offset++) {
                    const checkPos = pos + offset;
                    if (checkPos >= 0 && checkPos < state.doc.content.size) {
                      const n = state.doc.nodeAt(checkPos);
                      if (n && n.type.name === 'musicSheet') {
                        sheetPos = checkPos;
                        break;
                      }
                    }
                  }
                }
                const selection = NodeSelection.create(state.doc, sheetPos);
                dispatch(state.tr.setSelection(selection));
                if (event.dataTransfer) {
                  event.dataTransfer.setData('text/plain', 'music-sheet-block');
                  event.dataTransfer.effectAllowed = 'move';
                }
              }
            }
          } catch (err) {
            console.error("Error setting selection on musicSheet dragstart:", err);
          }
        }
        return false;
      },
      drop: (view, event) => {
        const clientX = event.clientX;
        const clientY = event.clientY;
        if (clientX === undefined || clientY === undefined) return false;
        
        try {
          const posObj = view.posAtCoords({ left: clientX, top: clientY });
          if (posObj) {
            const dropPos = posObj.pos;
            const { state, dispatch } = view;

            // 1) Handle Chord node drag-and-drop
            if (state.selection instanceof NodeSelection && state.selection.node.type.name === 'chord') {
              const chordNode = state.selection.node;
              const from = state.selection.from;
              
              let finalDropPos = dropPos;
              if (dropPos > from) {
                finalDropPos = Math.max(from, dropPos - chordNode.nodeSize);
              }
              
              const tr = state.tr;
              tr.delete(from, from + chordNode.nodeSize);
              tr.insert(finalDropPos, chordNode);
              dispatch(tr);
              
              event.preventDefault();
              return true;
            }

            // 2) Handle MusicSheet block node drag-and-drop
            if (state.selection instanceof NodeSelection && state.selection.node.type.name === 'musicSheet') {
              const sheetNode = state.selection.node;
              const from = state.selection.from;
              const nodeSize = sheetNode.nodeSize;
              
              const $drop = state.doc.resolve(dropPos);
              let targetBlockPos = dropPos;

              if ($drop.parent.isTextblock) {
                const targetDepth = $drop.depth;
                const startOfBlock = $drop.start(targetDepth);
                const endOfBlock = $drop.end(targetDepth);
                const mid = (startOfBlock + endOfBlock) / 2;

                if (dropPos <= mid) {
                  targetBlockPos = $drop.before(targetDepth);
                } else {
                  targetBlockPos = $drop.after(targetDepth);
                }
              }

              targetBlockPos = Math.max(0, Math.min(targetBlockPos, state.doc.content.size));

              let finalPos = targetBlockPos;
              if (finalPos > from) {
                finalPos = Math.max(from, finalPos - nodeSize);
              }

              if (finalPos !== from) {
                const tr = state.tr;
                tr.delete(from, from + nodeSize);
                tr.insert(finalPos, sheetNode);
                try {
                  const $newPos = tr.doc.resolve(finalPos);
                  tr.setSelection(new NodeSelection($newPos));
                } catch (se) {
                  // ignore
                }
                dispatch(tr);
                view.focus();
              }

              event.preventDefault();
              return true;
            }
          }
        } catch (err) {
          console.error("Error handling drop event:", err);
        }
        return false;
      },
      click: (view, event) => {
        const target = event.target;
        if (target && target.classList && target.classList.contains('comment-mark')) {
          const id = target.getAttribute('data-comment-id');
          showCommentsSidebar.value = true;
          if (id) {
            highlightCommentInDocument(id);
          }
        }
        return false;
      }
    }
  }
});

onMounted(() => {
  loadPinnedState();
});

defineExpose({
  editor
});
</script>

<style scoped>
.editor-layout-wrapper {
  display: flex;
  gap: 20px;
  width: 100%;
  height: 100%;
  min-height: 0;
  margin: 0px auto;
  align-items: stretch;
}

.editor-container {
  flex: 1;
  border: 1px solid #e2e8f0;
  overflow: visible;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
  display: flex;
  flex-direction: column;
  margin: 0 !important;
  height: 100%;
  min-height: 0;
}

.editor-container.no-border {
  border: none;
  box-shadow: none;
}

.editor-container.no-border :deep(.toolbar) {
  border-bottom: none;
}

/* Dark mode compatibility */
:deep(html.dark) .editor-container {
  border-color: #1e293b;
  background-color: #0f172a;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.2);
}
:deep(html.dark) .editor-container.no-border {
  border: none;
  box-shadow: none;
}

/* Editor Styling */
.editor-content-wrapper {
  padding: 24px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

@media (max-width: 768px) {
  .editor-content-wrapper {
    padding: 10px !important;
  }
}

:deep(html.dark) .editor-content-wrapper {
  background-color: #0f172a;
}

:deep(.ProseMirror) {
  min-height: 320px;
  outline: none;
  font-size: 16px;
  line-height: 1.625;
}

/* Base ProseMirror Element Styling */
:deep(.ProseMirror h1) {
  font-size: 2.25rem;
  font-weight: 700;
  line-height: 1.25;
  margin-top: 1.5rem;
  margin-bottom: 1rem;
}

:deep(.ProseMirror h2) {
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.35;
  margin-top: 1.5rem;
  margin-bottom: 0.75rem;
}

:deep(.ProseMirror h3) {
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.4;
  margin-top: 1.5rem;
  margin-bottom: 0.75rem;
}

:deep(.ProseMirror p) {
  margin-top: 0;
  margin-bottom: 1rem;
}

:deep(.ProseMirror ul), :deep(.ProseMirror ol) {
  padding-left: 1.5rem;
  margin-bottom: 1rem;
}

:deep(.ProseMirror ul) {
  list-style-type: disc;
}

:deep(.ProseMirror ol) {
  list-style-type: decimal;
}

:deep(.ProseMirror blockquote) {
  border-left: 4px solid #cbd5e1;
  padding-left: 1rem;
  margin-left: 0;
  margin-right: 0;
  color: #64748b;
  font-style: italic;
}

:deep(html.dark) .ProseMirror blockquote {
  border-left-color: #475569;
  color: #94a3b8;
}

/* Task lists style */
:deep(.ProseMirror ul[data-type="taskList"]) {
  list-style: none;
  padding: 0;
}

:deep(.ProseMirror ul[data-type="taskList"] li) {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 0.5rem;
}

:deep(.ProseMirror ul[data-type="taskList"] li > label) {
  user-select: none;
  margin-top: 3px;
}

:deep(.ProseMirror ul[data-type="taskList"] li > div) {
  flex: 1;
}

:deep(.ProseMirror mark) {
  background-color: #fef08a;
  color: #1c1917;
  padding: 0 2px;
  border-radius: 4px;
}

:deep(html.dark) .ProseMirror mark {
  background-color: #eab308;
  color: #0f172a;
}

:deep(.ProseMirror code) {
  background-color: #f1f5f9;
  color: #0f172a;
  padding: 2px 4px;
  border-radius: 4px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.875em;
}

:deep(html.dark) .ProseMirror code {
  background-color: #1e293b;
  color: #f8fafc;
}

:deep(.ProseMirror img) {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  margin: 1rem 0;
}

:deep(.ProseMirror p) {
  position: relative;
  line-height: 2.5em;
  margin-bottom: 0;
}

:deep(sup.chord-sup:not([data-spacer])),
:deep(sup.chord-sup[data-spacer=""]) {
  position: relative;
  border-bottom: 2px dotted red;
  border-style: dotted;
  /* display: contents; */
}

:deep(sup.chord-sup[data-spacer]:not([data-spacer=""])) {
  position: relative;
  border-bottom: none;
  /* display: contents; */
}

:deep(sup.chord-sup span.chord-span) {
  position: absolute;
  top: -0.6em;
  font-weight: bold;
  color: red;
  font-size: 18px;
  line-height: 1;
  cursor: grab;
  user-select: none;
  white-space: nowrap;
  -webkit-user-drag: element;
  touch-action: none;
}

:deep(sup.chord-sup span.chord-span:active) {
  cursor: grabbing;
}

/* Style for selected chord node */
:deep(sup.chord-sup.ProseMirror-selectednode span.chord-span) {
  outline: 2px solid #6366f1;
  outline-offset: 2px;
  border-radius: 2px;
}



:deep(.ProseMirror blockquote) {
  border-left: none;
}

/* Inline chord representations for specific blockquote sections */
:deep(.ProseMirror blockquote.custom-blockquote.q-intro sup.chord-sup),
:deep(.ProseMirror blockquote.custom-blockquote.q-arrange sup.chord-sup),
:deep(.ProseMirror blockquote.custom-blockquote.q-solo sup.chord-sup),
:deep(.ProseMirror blockquote.custom-blockquote.q-finishing sup.chord-sup) {
  vertical-align: baseline;
  font-size: inherit;
  top: 0;
}

:deep(.ProseMirror blockquote.custom-blockquote.q-intro sup.chord-sup span.chord-span),
:deep(.ProseMirror blockquote.custom-blockquote.q-arrange sup.chord-sup span.chord-span),
:deep(.ProseMirror blockquote.custom-blockquote.q-solo sup.chord-sup span.chord-span),
:deep(.ProseMirror blockquote.custom-blockquote.q-finishing sup.chord-sup span.chord-span) {
  position: relative;
  top: unset;
  display: inline-block;
  margin: 0 4px;
  vertical-align: baseline;
}

:deep(.ProseMirror blockquote.custom-blockquote) {
  position: relative;
  padding-left: 36px;
  margin: 1.5rem 0;
  min-height: 40px;
  border-left: none;
  font-style: normal;
  color: inherit !important;
}

:deep(.ProseMirror blockquote.custom-blockquote em),
:deep(.ProseMirror blockquote.custom-blockquote i) {
  font-style: italic !important;
}

:deep(.ProseMirror blockquote.custom-blockquote::before) {
  content: attr(data-type);
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 26px;
  border-right: 2px dashed var(--blockquote-color, #6366f1);
  writing-mode: vertical-rl;
  text-orientation: mixed;
  transform: rotate(180deg);
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: var(--blockquote-color, #6366f1);
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  user-select: none;
  white-space: nowrap;
  letter-spacing: 0.05em;
  padding-right: 4px;
}

:deep(.ProseMirror blockquote.custom-blockquote.q-chorus) { 
  --blockquote-color: #3b82f6; 
  font-weight: bold;
}
:deep(.ProseMirror blockquote.custom-blockquote.q-verse) { --blockquote-color: #10b981; }
:deep(.ProseMirror blockquote.custom-blockquote.q-intro) { --blockquote-color: #6366f1; }
:deep(.ProseMirror blockquote.custom-blockquote.q-arrange) { --blockquote-color: #6366f1; }
:deep(.ProseMirror blockquote.custom-blockquote.q-bridge) { 
  --blockquote-color: #f59e0b; 
  font-style: italic;
}
:deep(.ProseMirror blockquote.custom-blockquote.q-prelude) { 
  --blockquote-color: #8b5cf6; 
  font-style: italic;
}
:deep(.ProseMirror blockquote.custom-blockquote.q-solo) { --blockquote-color: #ec4899; }
:deep(.ProseMirror blockquote.custom-blockquote.q-note) {
  --blockquote-color: #64748b;
  background-color: rgba(255, 255, 176, 0.5);
  border: solid 1px rgb(229, 229, 67);
  border-radius: 4px;
}
:deep(.ProseMirror blockquote.custom-blockquote.q-finishing) { --blockquote-color: #ef4444; }

/* Fallback type labels when data-type attribute is missing */
:deep(.ProseMirror blockquote.custom-blockquote.q-chorus:not([data-type])::before) { content: "chorus"; }
:deep(.ProseMirror blockquote.custom-blockquote.q-verse:not([data-type])::before) { content: "verse"; }
:deep(.ProseMirror blockquote.custom-blockquote.q-intro:not([data-type])::before) { content: "intro"; }
:deep(.ProseMirror blockquote.custom-blockquote.q-arrange:not([data-type])::before) { content: "arrange"; }
:deep(.ProseMirror blockquote.custom-blockquote.q-bridge:not([data-type])::before) { content: "bridge"; }
:deep(.ProseMirror blockquote.custom-blockquote.q-prelude:not([data-type])::before) { content: "prelude"; }
:deep(.ProseMirror blockquote.custom-blockquote.q-solo:not([data-type])::before) { content: "solo"; }
:deep(.ProseMirror blockquote.custom-blockquote.q-note:not([data-type])::before) { content: "note"; }
:deep(.ProseMirror blockquote.custom-blockquote.q-finishing:not([data-type])::before) { content: "finishing"; }

/* Spacer display rules */
:deep(sup.chord-sup::after) {
  content: attr(data-spacer);
  white-space: pre;
  user-select: none;
  pointer-events: none;
  font-weight: bold;
  color: inherit;
}

:deep(sup.chord-sup[data-spacer*=" "]::after) {
  border-bottom: 2px dashed currentColor;
}

:deep(sup.chord-sup[data-spacer*="_"]::after) {
  border-bottom: none;
}

:deep(.ProseMirror.measure-chords sup.chord-sup::after) {
  content: "" !important;
}

/* Comments Sidebar Styling */
.comments-sidebar {
  width: 260px;
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 500px;
  overflow-y: auto;
  flex-shrink: 0;
}

:deep(html.dark) .comments-sidebar {
  background-color: #0f172a;
  border-color: #1e293b;
}

.comments-title {
  font-size: 14px;
  font-weight: 700;
}

.sidebar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.close-sidebar-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 4px;
  transition: background-color 0.15s ease;
}

.close-sidebar-btn:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

:deep(html.dark) .close-sidebar-btn:hover {
  background-color: rgba(255, 255, 255, 0.08);
}

.comments-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.comment-card {
  border-radius: 8px;
}

/* Comment Mark styling in editor */
:deep(.ProseMirror span.comment-mark) {
  border-bottom: 2px dashed #f59e0b;
  background-color: rgba(245, 158, 11, 0.12);
  cursor: pointer;
  padding: 0 2px;
  border-radius: 2px;
  transition: all 0.2s ease;
}

:deep(.ProseMirror span.comment-mark.comment-mark-active) {
  background-color: rgba(245, 158, 11, 0.4) !important;
  box-shadow: 0 0 0 2px #f59e0b;
}

/* Vocal Highlight styling in editor */
:deep(.ProseMirror span.vocal1),
:deep(.ProseMirror span.vocal2),
:deep(.ProseMirror span.vocal3),
:deep(.ProseMirror span.vocal4),
:deep(.ProseMirror span.vocal5),
:deep(.ProseMirror span.vocal6),
:deep(.ProseMirror span.vocal7),
:deep(.ProseMirror span.vocal8),
:deep(.ProseMirror span.vocal9),
:deep(.ProseMirror span.vocal10) {
  position: relative;
  border-radius: 4px;
  padding: 0 4px;
}

/* Vocal Label indicator */
:deep(.ProseMirror span[class*="vocal"])::after {
  font-size: 10px;
  font-weight: 700;
  margin-left: 6px;
  display: inline-flex;
  align-items: center;
  height: 18px;
  padding: 0 6px;
  border-radius: 4px;
  user-select: none;
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  vertical-align: middle;
  line-height: 1;
}

:deep(html.dark) .ProseMirror span[class*="vocal"]::after {
  background-color: #1e293b;
  border-color: #334155;
}



/* Customize label color per voice */
:deep(.ProseMirror span.vocal1)::after { content: " \2190  Voz 1"; color: var(--vocal1-color-text); border-color: rgba(155, 158, 232, 0.4); }
:deep(.ProseMirror span.vocal2)::after { content: " \2190  Voz 2"; color: var(--vocal2-color-text); border-color: rgba(231, 187, 187, 0.4); }
:deep(.ProseMirror span.vocal3)::after { content: " \2190  Voz 3"; color: #0d9488; border-color: rgba(13, 148, 136, 0.4); }
:deep(.ProseMirror span.vocal4)::after { content: " \2190  Voz 4"; color: var(--vocal4-color-text); border-color: rgba(239, 168, 106, 0.4); }
:deep(.ProseMirror span.vocal5)::after { content: " \2190  Voz 5"; color: var(--vocal5-color-text); border-color: rgba(126, 212, 202, 0.4); }
:deep(.ProseMirror span.vocal6)::after { content: " \2190  Voz 6"; color: var(--vocal6-color-text); border-color: rgba(160, 198, 120, 0.4); }
:deep(.ProseMirror span.vocal7)::after { content: " \2190  Voz 7"; color: var(--vocal7-color-text); border-color: rgba(198, 212, 126, 0.4); }
:deep(.ProseMirror span.vocal8)::after { content: " \2190  Voz 8"; color: var(--vocal8-color-text); border-color: rgba(126, 212, 202, 0.4); }
:deep(.ProseMirror span.vocal9)::after { content: " \2190  Voz 9"; color: var(--vocal9-color-text); border-color: rgba(155, 126, 212, 0.4); }
:deep(.ProseMirror span.vocal10)::after { content: " \2190  Voz 10"; color: var(--vocal10-color-text); border-color: rgba(212, 126, 196, 0.4); }

:deep(.ProseMirror span.vocal1) { background-color: var(--vocal1-color); }
:deep(.ProseMirror span.vocal2) { background-color: var(--vocal2-color); }
:deep(.ProseMirror span.vocal3) { background-color: var(--vocal3-color); }
:deep(.ProseMirror span.vocal4) { background-color: var(--vocal4-color); }
:deep(.ProseMirror span.vocal5) { background-color: var(--vocal5-color); }
:deep(.ProseMirror span.vocal6) { background-color: var(--vocal6-color); }
:deep(.ProseMirror span.vocal7) { background-color: var(--vocal7-color); }
:deep(.ProseMirror span.vocal8) { background-color: var(--vocal8-color); }
:deep(.ProseMirror span.vocal9) { background-color: var(--vocal9-color); }
:deep(.ProseMirror span.vocal10) { background-color: var(--vocal10-color); }

/* MusicSheet block node: drag & drop affordances */
:deep(.ProseMirror .music-sheet-node-view) {
  position: relative;
}
:deep(.ProseMirror .music-sheet-node-view.ProseMirror-selectednode) {
  outline: 2px solid #6366f1;
  outline-offset: 4px;
  border-radius: 8px;
}
:deep(.ProseMirror [data-drag-handle]) {
  cursor: grab;
  touch-action: none;
}
:deep(.ProseMirror [data-drag-handle]:active) {
  cursor: grabbing;
}
/* Drop indicator rendered by ProseMirror's Dropcursor */
:deep(.ProseMirror .ProseMirror-dropcursor) {
  border-left: 2px solid #6366f1;
  margin-left: -1px;
  pointer-events: none;
}
</style>
